import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

const PWAContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

export function usePWA() {
  return useContext(PWAContext);
}

function detectIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !('MSStream' in window);
}
function detectStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
}
function detectOS() {
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (/android/.test(ua)) return 'android';
  if (/macintosh|windows|linux/.test(ua)) return 'desktop';
  return 'unknown';
}

async function trackInstall(source) {
  try {
    await fetch(`${API_URL}/api/pwa/track-install`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ os: detectOS(), user_agent: navigator.userAgent, source }),
    });
  } catch (_) {}
}

export function PWAProvider({ children }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled]       = useState(false);
  const [isIOS]                             = useState(detectIOS);
  const [showIOSModal, setShowIOSModal]     = useState(false);
  const dismissed = useRef(!!localStorage.getItem('pwa-install-dismissed'));

  useEffect(() => {
    // Verificar se já está instalado
    if (detectStandalone()) { setIsInstalled(true); return; }

    // Usar prompt já capturado antes do React montar (index.html script)
    if (window.__pwaInstallPrompt) {
      setDeferredPrompt(window.__pwaInstallPrompt);
    }

    // Android/Chrome — capturar eventos futuros
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    // Evento customizado do script no <head>
    const onCustom = (e) => setDeferredPrompt(e.detail);

    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('pwa-prompt-ready', onCustom);

    // Detectar instalação concluída (Android/Chrome nativo)
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      window.__pwaInstallPrompt = null;
      localStorage.setItem('pwa-install-dismissed', '1');
      trackInstall('native_prompt');
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('pwa-prompt-ready', onCustom);
    };
  }, []);

  /** Instala no Android (1 clique) ou mostra guia iOS/Android */
  const install = async () => {
    if (isInstalled) return;

    // Android/Chrome com prompt nativo disponível → 1 clique
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
        localStorage.setItem('pwa-install-dismissed', '1');
        trackInstall('native_prompt');
      }
      setDeferredPrompt(null);
      return;
    }

    // iOS, Android sem prompt, ou qualquer outro browser → mostrar guia
    setShowIOSModal(true);
  };

  // Chamado pelo IOSInstallModal quando o utilizador confirma a instalação iOS
  const confirmIOSInstall = () => {
    trackInstall('ios_guide');
    setIsInstalled(true);
    localStorage.setItem('pwa-install-dismissed', '1');
    setShowIOSModal(false);
  };

  const dismiss = () => {
    dismissed.current = true;
    localStorage.setItem('pwa-install-dismissed', '1');
  };

  const isInstallable = !isInstalled && (!!deferredPrompt || isIOS);
  const wasDismissed  = dismissed.current;

  return (
    <PWAContext.Provider value={{
      isInstallable, isInstalled, isIOS,
      showIOSModal, setShowIOSModal,
      install, dismiss, wasDismissed,
      confirmIOSInstall,
    }}>
      {children}
    </PWAContext.Provider>
  );
}
